import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AccountUserInfo, UpdateAccount, ChangePassword } from "../shared/models/customerModels";
import { serializeError, createErrorNotification } from "../../shared/utils/errorUtils";
import customersApi from "../shared/api/customersApi";
import { SerializedError } from '../../shared/models/errorHandlingModels';

interface CustomerAccountState {
  isLoading: boolean;
  isSaving: boolean;
  success?: string;
  error?: any;
  userInfo?: AccountUserInfo | null;
  openSnackbar: boolean;
  alertMessage: string;
  severity: "error" | "success";
  showOldPassword: boolean;
  showNewPassword: boolean;
  showConfirmPassword: boolean;
}

const initialState: CustomerAccountState = {
  isLoading: false,
  isSaving: false,
  success: undefined,
  error: undefined,
  userInfo: null,
  openSnackbar: false,
  alertMessage: "",
  severity: "error",
  showOldPassword: false,
  showNewPassword: false,
  showConfirmPassword: false,
};

export const getAccount = createAsyncThunk(
  'customerAccount/getAccount',
  async (_, { rejectWithValue }) => {
    try {
      const result = await customersApi.getAccount();
      return result;
    } catch (error: any) {
      return rejectWithValue(serializeError(error));
    }
  }
);

export const updateAccount = createAsyncThunk(
  'customerAccount/updateAccount',
  async (model: UpdateAccount, { rejectWithValue }) => {
    try {
      const result = await customersApi.updateAccount(model);
      return result;
    } catch (error: any) {
      return rejectWithValue(serializeError(error));
    }
  }
);

export const changePassword = createAsyncThunk(
  'customerAccount/changePassword',
  async (model: ChangePassword, { rejectWithValue }) => {
    try {
      const result = await customersApi.changePassword(model);
      return result;
    } catch (error: any) {
      return rejectWithValue(serializeError(error));
    }
  }
);

const customerAccountSlice = createSlice({
  name: 'customerAccount',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = undefined;
    },
    clearSuccess: (state) => {
      state.success = undefined;
    },
    resetState: (state) => {
      state.isLoading = false;
      state.isSaving = false;
      state.success = undefined;
      state.error = undefined;
      state.userInfo = null;
      state.openSnackbar = false;
      state.alertMessage = "";
      state.severity = "error";
    },
    showNotification: (state, action) => {
      state.openSnackbar = action.payload.openSnackbar;
      state.severity = action.payload.severity;
      state.alertMessage = action.payload.alertMessage;
    },
    setOpenSnackbar: (state, action) => {
      state.openSnackbar = action.payload;
    },
    toggleOldPasswordVisibility: (state) => {
      state.showOldPassword = !state.showOldPassword;
    },
    toggleNewPasswordVisibility: (state) => {
      state.showNewPassword = !state.showNewPassword;
    },
    toggleConfirmPasswordVisibility: (state) => {
      state.showConfirmPassword = !state.showConfirmPassword;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAccount.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(getAccount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userInfo = action.payload;
      })
      .addCase(getAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.openSnackbar = true;
        state.severity = 'error';
        const errorNotification = createErrorNotification(action.payload as SerializedError);
        state.alertMessage = errorNotification.message || 'accountLoadFailed';
      })

      .addCase(updateAccount.pending, (state) => {
        state.isSaving = true;
        state.error = undefined;
        state.success = undefined;
      })
      .addCase(updateAccount.fulfilled, (state, action) => {
        state.isSaving = false;
        state.success = "accountUpdated";
        state.openSnackbar = true;
        state.severity = 'success';
        state.alertMessage = "accountUpdated";
      })
      .addCase(updateAccount.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
        state.openSnackbar = true;
        state.severity = 'error';
        const errorNotification = createErrorNotification(action.payload as SerializedError);
        state.alertMessage = errorNotification.message || 'accountUpdateFailed';
      })

      .addCase(changePassword.pending, (state) => {
        state.isSaving = true;
        state.error = undefined;
        state.success = undefined;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.isSaving = false;
        state.success = "passwordChanged";
        state.openSnackbar = true;
        state.severity = 'success';
        state.alertMessage = "passwordChanged";
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
        state.openSnackbar = true;
        state.severity = 'error';
        const errorNotification = createErrorNotification(action.payload as SerializedError);
        state.alertMessage = errorNotification.message || 'passwordChangeFailed';
      });
  },
});

export const {
  clearError,
  clearSuccess,
  resetState,
  showNotification,
  setOpenSnackbar,
  toggleOldPasswordVisibility,
  toggleNewPasswordVisibility,
  toggleConfirmPasswordVisibility,
} = customerAccountSlice.actions;

export default customerAccountSlice.reducer;
